<template>
  <div
    class="aha-presentation-editor-wrapper"
    :class="{
      'show-grid-slide': showingSlideGrid,
      'right-nav--opening': rightNavActiveTab,
      'mobile-active': $store.state.activeModelDetailSlide,
      'powerpoint-addin': isEnableAddinPowerpoint,
    }"
  >
    <slot name="SocketError"></slot>
    <slot name="WarningParticipantLimit"></slot>
    <slot name="SignUpCTAHeader"></slot>
    <slot name="EditorHeaderWrapper"></slot>
    <slot name="PublicAnnouncement"></slot>

    <div class="editor__background">
      <slot name="SideLeft"></slot>

      <div
        class="middle-side-right__wrapper"
        :class="{
          'flex-1': !$store.state.indicators.showingSlideGrid,
          'powerpoint-addin': isEnableAddinPowerpoint,
        }"
      >
        <slot name="SlideSearchPopup"></slot>
        <slot name="AIReachRequestLimit"></slot>
        <slot name="ImportStatusBox"></slot>
        <slot name="Middle"></slot>
        <slot name="RightPanelContainer"></slot>
        <slot name="RightNav"></slot>
        <slot name="ContentEditPanel"></slot>
      </div>
    </div>
    <DocToSlideImportModal
      v-if="isDocToSlideImportModalVisible"
      :docToSlideResponseError="docToSlideResponseError"
      v-aha-emit-action.view
      name="doc_to_slide_import_modal"
      @clickOnCloseDocToSlideImportModal="clickOnCloseDocToSlideImportModal"
    />
    <BeTakenOverModal
      v-if="isShowBeTakenOverModal"
      @hide="closeBeTakenOverModal()"
      :presenter="tookOverPresenter"
      :beTakenOverWhilepresenting="beTakenOverWhilepresenting"
    />

    <slot name="UserInactiveReloadModal"></slot>
    <slot name="AIDocToSlideResultModal"></slot>
    <slot name="TemplateLibraryModal"></slot>

    <PresenterWindowNotifyModal
      v-if="showPresenterWindowNotifyModal"
      @hide="showPresenterWindowNotifyModal = false"
    />
    <MSTeamsReloadModal
      v-if="isMSTeamsApp && isShowMSTeamsReloadModal"
      :isReloading="isReloadingMSTeams"
      @ok="() => { msTeams.reloadPresentingScreen(this) }"
    />
    <MSTeamsSharingGuideModal
      v-if="showMSTeamsSharingGuidePopup"
      @hide="showMSTeamsSharingGuidePopup = false"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import Vue from 'vue'

import * as Sentry from '@sentry/vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import moment from 'moment'
import PresentationApi from '@/api/presentation'
import ImportPPTMixin from '@/mixins/presentation/ImportPPTMixin'

// components
import BeTakenOverModal from '@/components-v2/presentation-editor/collaboration/BeTakenOverModal.vue'
import socketHelper from '@/helpers/socket/socket-helper'
import DocToSlideImportModal from '@/components-v2/modals/DocToSlideImportModal.vue'

// handler
import PresentationEditorSocketHandler from '@/utils/presentationEditorSocketHandler.v2.js'

// configs
import Config from '@/config'

// utils
import Utils from '@/util'
import PresentationUtils from '@/utils/presentation'
import PresentationDetailUtils from '@/utils/PresentationDetailUtils'
import OpenAIUtils from '@/utils/open-ai/OpenAIUtils'
import GetDataPresentationDetailUtils from '@/utils/GetDataPresentationDetailUtils'
import RenderCanvasBlocksUtils from '@/utils/renderCanvasBlocksUtils'
import MSTeamsSharingGuideModal from '@/components-v2/ms-teams/MSTeamsSharingGuideModal.vue'

// socket
import Socket from '@/socket'

// pricing
import Pricing from '@/pricing'

import office from '@/services/office'

// event bus
import EventBus from '@/eventBus'

// services
import { handleLoadAllImageCache } from '@/utils/indexedDB/image.cache.utils'

// constants
import constants from '@/constant'
import MSTeamsReloadModal from '@/components-v2/ms-teams/MSTeamsReloadModal.vue'
import PresenterWindowNotifyModal from './collaboration/PresenterWindowNotifyModal.vue'
import eventCategories from '@/constant/eventCategories'
import msTeams from '@/utils/ms-teams'
import { detectSafari } from '@/utils/dom.utils'
import { KEYCODES } from '@/constant/keycode.constant'
import workspace from '@/constant/workspace'
import { preLoadFontsForPresentationDetail } from '@/utils/font.util'

// API
import SlideApi from '@/api/slide'

const noAudioTypes = new Set(constants.slideTypesNoAudio)

export default {
  components: {
    BeTakenOverModal,
    PresenterWindowNotifyModal,
    MSTeamsReloadModal,
    MSTeamsSharingGuideModal,
    DocToSlideImportModal,
  },
  mixins: [ImportPPTMixin],
  props: {
    isEnableEditPresentationWithOtherRouter: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      refer: null,
      activeTab: 1,
      quizwordcloudDebound: null,
      isDebouncing: false,
      isDebouncingReaction: false,
      isDebouncingOnlineCount: false,
      isDebouncingGaOnlineCount: false,
      onlineCount: 0,
      showModalUploadImg: false,
      sockets: {},
      isShowBeTakenOverModal: false,
      beTakenOverWhilepresenting: false,
      tookOverPresenter: null,
      showPresenterWindowNotifyModal: false,
      arrowPos: 0,
      isShowMSTeamsReloadModal: false,
      isReloadingMSTeams: false,
      showMSTeamsSharingGuidePopup: false,
    }
  },
  computed: {
    ...mapState({ // Do not delete anything here, since they might be called via imported functions
      presentation: (state) => state.presentation,
      presentations: (state) => state.presentations,
      sharePresentation: (state) => state.sharePresentation,
      isNewPresentation: (state) => state.isNewPresentation,
      showingSlideGrid: (state) => state.indicators.showingSlideGrid,
      slideActive: (state) => state.slideActive,
      socketId: (state) => state.socketId,
      currentUser: (state) => state.currentUser,
      userOnline: (state) => state.userOnline,
      onlineStatus: (state) => state.onlineStatus,
      plan: (state) => state.plan,
      isPresenterWhiteLabel: (state) => state.isPresenterWhiteLabel,
      workingMode: (state) => state.workingMode,
      isAdmin: (state) => state.isAdmin,
      isPlayDemoMode: (state) => state.isPlayDemoMode,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
      userInactiveReload: (state) => state.userInactiveReload,
      isMSTeamsApp: (state) => state.isMSTeamsApp,
      isEnableAddinPowerpoint: (state) => state.isEnableAddinPowerpoint,
      isOpenModalUpgradePlan: (state) => state.isOpenModalUpgradePlan,
      importStatus: (state) => state.importStatus,
      docToQuizNewPresentationRequestId: (state) => state.docToQuizNewPresentationRequestId,
      docToQuizGlobalImportError: (state) => state.docToQuizGlobalImportError,
      planBenefit: (state) => state.planBenefit,
      isShowingSlideSearchPopup: (state) => state.isShowingSlideSearchPopup,
      integrationPlatform: (state) => state.integrationPlatform,
      renderPresentationDetail: (state) => state.renderPresentationDetail,
    }),
    ...mapGetters(['isPresenting', 'isBackstage', 'selectedSlides']),
    ...mapGetters('zoomApp', ['isZoomReady']),
    ...mapGetters('rightNav', ['rightNavTabNames', 'rightNavActiveTab']),
    ...mapGetters('progressBar', ['loadingProgressBar']),
    ...mapGetters('docToSlideGenerator', [
      'isDocToSlideResultModalVisible',
      'isDocToSlideDoneGenerating',
      'docToSlideResponse',
      'docToSlideContentLanguage',
      'isDocToSlideSubmittingFeedback',
      'isDocToSlideResponseWithError',
      'docToSlideResponseError',
      'docToSlideCustomAIStumpedText',
      'isDocToSlideLoadingSpinnerHidden',
      'isDocToSlideImportModalVisible',
    ]),
    ...mapGetters('collaboration', ['isTakeOverToolbarVisible']),
    ...mapGetters('history', ['canUseUndoRedoV2']),
    ...mapGetters('slideAnnotations', ['canUseSlideAnnotations']),

    isQuizLobbyScreen() {
      return this.slideActive.type === 'pickAnswer'
        && this.slideActive.quizStatus === constants.QuizStatuses.LOBBY_SCREEN
    },
    isQuizResultScreen() {
      return this.slideActive.type === 'pickAnswer'
        && this.slideActive.quizStatus === constants.QuizStatuses.RESULT_SCREEN
    },
    isQuizNotLobbyScreen() {
      return this.slideActive.type == 'pickAnswer'
            && this.slideActive.quizStatus != constants.QuizStatuses.LOBBY_SCREEN
    },
    isBlankPresentation() {
      const { isNewPresentation } = this.$store.state
      const { slides } = this.$store.state.presentation
      return isNewPresentation && slides.every((s) => !s.type)
    },
    isDesktopPresenting() {
      return window.innerWidth > 800 &&
        !this.isBackstage &&
        !Utils.getParamValue('report', window.location.href) // open report panel
    },
    isTypeAnswerSlideType() {
      return this.slideActive.type === 'pickAnswer' && this.slideActive.slideType === 'typeAnswer'
    },
    showPopupUserInactive() {
      return !this.isPresenting &&
        this.currentUser.accountRole !== workspace.VIEWER &&
        this.userInactiveReload
      && moment().add(-this.timeShowPopupInactive, 'minutes').diff(moment(this.joinedAt)) >= 0
    },
    showingAudioTab() {
      return this.selectedSlides.some(
        (s) => s.id && !noAudioTypes.has(s.type),
      )
    },
    isRightPanelContainerVisible() {
      if (this.isEnableAddinPowerpoint && this.isPresenting) return false
      return this.currentUser.id && !this.isBackstage && this.rightNavActiveTab
    },
  },
  watch: {
    slideActive() {
      GetDataPresentationDetailUtils.checkShowQuizCorrectAnswerIconReminder(this)
      // Re-set slideActive.activeTab on takeover due to legacy dependency across the project
      if (this.rightNavActiveTab && !this.slideActive.activeTab) {
        this.setRightNavActiveTabAction(this.rightNavActiveTab)
      }
    },
    renderPresentationDetail() {
      if (!this.renderPresentationDetail) return
      if (this.isDocToSlideDoneGenerating && this.docToSlideResponse && this.docToSlideResponse.length) {
        this.toggleDocToSlideResultModalAction(true)
        EventBus.$emit('doc-to-quiz: close_generating_toast')
      }
    },
    isDocToSlideDoneGenerating() {
      if (!this.isDocToSlideDoneGenerating) return
      if (this.docToSlideResponse && this.docToSlideResponse.length) {
        this.toggleDocToSlideResultModalAction(true)
        EventBus.$emit('doc-to-quiz: close_generating_toast')
      }
    },
  },
  created() {
    this.msTeams = msTeams
    // prevent editor from scrolling
    document.body.classList.add('overflow-hidden')
    document.body.classList.add('fixed') // https://ahaslides.atlassian.net/browse/AHA-2696 -> fix simiar to using legacy's .no-scroll
    this.preloadListImagePreview()
    this.syncWorkingMode()
    if (sessionStorage.getItem('zoomApp') == 'true') {
      this.configZoomSdkAction()
    }

    preLoadFontsForPresentationDetail()
  },
  destroyed() {
    document.body.classList.remove('overflow-hidden')
    document.body.classList.remove('fixed') // https://ahaslides.atlassian.net/browse/AHA-2696 -> fix simiar to using legacy's .no-scroll
    document.body.classList.remove('presentation-detail-layout')
    PresentationEditorSocketHandler.disconnectSocket(this)
    EventBus.$off('onKeyboard')
    EventBus.$off('updateNextSlide')
    EventBus.$off('getDataPresentationDetail')
    EventBus.$off('slide-search-popup:open')
    document.removeEventListener('keydown', this.keyDown)
    this.$store.state.audiences = {}
    this.$store.state.userOnline = 0
    this.$store.state.questions = []
    this.$store.state.tabQnaQuestion = 1
    this.$store.state.lastQuestion = {}
    this.$store.state.questionCount = 0
    this.$store.state.showLiveAudienceDemo = false
    this.$store.state.showQnaSession = false
    this.$store.state.presentation.id = null
    this.$store.state.presentationEditors = []
    this.$store.state.isDisableEditPresentation = false
    this.$store.state.openImportModal = false
    this.clearPresentationReportAction()
    this.$store.commit('unsetPresentation')
    this.clearPublicSlideTemplateAction()
    this.resetAIConversation()
    // Clear cached presentation ID to prevent stale data in AI chatbot URL
    this.clearLastKnownPresentationId()
  },
  async mounted() {
    this.registerEventHistory()

    this.$store.state.sharePresentation = false
    this.$store.state.isNewPresentation = Utils.getParamValue('new', window.location.href) === 'true'
    if (this.$store.state.isNewPresentation && window.history.replaceState) {
      window.history.replaceState('', '', `/presentation/${this.$route.params.id}`)

      // after created new presentation then set flag = false
      this.$store.commit('setIsCreatingNewPresentation', false)
    }

    document.body.classList.add('presentation-detail-layout')

    document.onkeydown = (e) => {
      if (
        this.$store.state.slideActive.isFocusingInput &&
        Config.DISABLE_ON_FOCUS_SLIDE_KEYS.includes(e.code)
      ) {
        return
      }
      EventBus.$emit('onKeyboard', e)
    }

    this.$store.state.isTouchScreen = Utils.checkTouchScreen()
    detectSafari()
    PresentationEditorSocketHandler.handleSocketEvent(this)

    EventBus.$on('updateNextSlide', _.debounce(this.handleUpdateNextSlide, 400, { leading: true, trailing: true }))
    EventBus.$on('getDataPresentationDetail', async (callback, trigger) => {
      await this.getDataPresentationDetail(`EventBus event from ${trigger}`)
      if (callback) {
        callback()
      }
    })
    EventBus.$on('slide-search-popup:open', this.openSlideSearchPopup)
    await this.detectPowerpoint()
    // Now load
    await this.getDataPresentationDetail('PresentationEditor.vue@mounted')
    if (this.canUseSlideAnnotations) {
      await this.fetchAnnotationsAction()
    }

    this.initSocket(this.presentation.uniqueAccessCode)
    this.joinCollaborativeEditingRoom(this.presentation)

    if (this.isBackstage) {
      if (!JSON.parse(localStorage.getItem('PresenterWindowNotifyModal'))) {
        setTimeout(() => {
          this.showPresenterWindowNotifyModal = true
        }, 1000)
      }
    }
    if (Utils.isPageReloaded()) {
      socketHelper.backstageControl({
        uniqueAccessCode: this.presentation.uniqueAccessCode,
        type: 'reload',
      })
    }

    if (this._isPresentationEditorPage(this.$route)
      && this.currentUser && this.currentUser.id && !this.presentation.takenOverBy && this.presentation.id) {
      PresentationApi.takeOver(this.presentation.id)
    }

    // Support realtime in "share" view
    if (this.$route.name === 'PresentationShare') {
      // Using 'join-user' event for convenient :(
      Socket.getSocket().emit('join-user', `${this.$store.state.presentation.uniqueAccessCode}-presenter`)
    }
    // dipslay slide search by default if just created a blank prez
    if (this.isBlankPresentation) {
      this.$store.state.isShowingSlideSearchPopup = true
      this.patchPresentationAction({
        isShowingSlideSearch: true,
      })
    }

    this.handleTrackingReferPublicTemplate()
    msTeams.initialize(this)

    this.$store.state.plan = Pricing.getPlanByType(
      this.$store.state.currentUser.plan,
    )

    this.handleImporting(this.$store.state.currentUser.id)

    if (this.currentUser.accountId) {
      this.getCurrentTeamAction(this.currentUser.accountId)
    }

    if (this.$store.state.isNewPresentation && this.$store.state.isEnableAddinPowerpoint) {
      this.$store.state.isOpenEditSlidePowerPoint = true
    }
    this.broadcastUpdatePlan()
  },
  methods: {
    ...mapActions([
      'getCurrentUserAction',
      'getCurrentTeamAction',
      'patchPresentationAction',
      'setErrorOnInternalServerAction',
      'updateSlideActiveAction',
      'updateSlideByIdAction',
      'patchSlideByIdAction',
      'checkSlideMovingDirectionAction',
      'undoAction',
      'redoAction',
      'clearHistory',
      'getLimitationPresentationAction',
      'updateCurrentUserAction',
      'getPresentationByIdAction',
      'recheckAndFixCorrectOrderSlidesAction',
    ]),
    ...mapActions('presentationReport', ['clearPresentationReportAction']),
    ...mapActions('presentationChat', ['audienceChangeStatusAction']),
    ...mapActions('contentSlideTemplates', ['clearPublicSlideTemplateAction']),
    ...mapActions('zoomApp', [
      'configZoomSdkAction',
    ]),
    ...mapActions('rightNav', ['setRightNavActiveTabAction', 'showSlideTypeSelectionAction']),
    ...mapActions('slideInsertionToPresentation', ['addSlideToPresentationAction']),
    ...mapActions('docToSlideGenerator', [
      'handleDocToSlideErrorAction',
      'toggleDocToSlideResultModalAction',
      'setDocToSlideResponseAction',
      'docToSlideGenerateSlidesFromFeedbackAction',
      'generateDocToSlideUniqueRequestIdAction',
      'setDocToSlideImportStageAction',
      'setDocToSlideResponseErrorAction',
      'toggleDocToSlideImportModalAction',
      'resetDocToSlideRequestIdAction',
    ]),
    ...mapActions('history', ['undoSlidesAction', 'redoSlidesAction']),
    ...mapActions('promptToSlideGenerator', ['resetPromptToSlideRequestAction']),
    ...mapActions('aiConversation', ['resetConversationAction']),
    ...mapActions('featureToggles', ['updateFeatureContext']),
    ...mapActions('slideAnnotations', ['fetchAnnotationsAction', 'syncAnnotationsAction']),
    ...mapActions('aiChatbot', ['clearLastKnownPresentationId']),

    registerEventHistory() {
      this.clearHistory()
      document.addEventListener('keydown', this.keyDown)
    },
    updatePresentationSessionOnlineCount: _.throttle((presentationId, onlineCount) => {
      PresentationApi.updateOnlineCountByPresentationId(presentationId, { onlineCount })
    }, 5000),
    handleTrackingReferPublicTemplate() {
      this.refer = Utils.getParamValue('refer', window.location.href)
      if (this.refer === constants.referPublicTemplate) {
        const query = { ...this.$route.query }
        delete query.refer
        this.$router.replace({ query })
        this.$store.state.isReferPublicTemplate = true
        Utils.mixpanelTracking(eventCategories.CATEGORY_PUBLIC_TEMPLATE_DEMO_MODE, 'Create new presentation', 'Use public template')
      }
    },
    keyDown(e) {
      const evtObj = window.event ? window.event : e
      if ((evtObj.ctrlKey || e.metaKey) && ((evtObj.keyCode === 90 && evtObj.shiftKey) || (evtObj.keyCode === 89))) {
        e.preventDefault()
        if (this.canUseUndoRedoV2) {
          this.redoSlidesAction()
        } else {
          this.redoAction()
        }

        return
      }
      if (evtObj.keyCode === 90 && (evtObj.ctrlKey || e.metaKey)) {
        e.preventDefault()
        if (this.canUseUndoRedoV2) {
          this.undoSlidesAction()
        } else {
          this.undoAction()
        }
        Vue.toasted.clear()
        return
      }

      this.handlePresentHotkey(e)
    },
    handlePresentHotkey(e) {
      const hasNoneFocusedElement = document.activeElement === document.body || document.activeElement === null
      if (e.shiftKey && e.keyCode === KEYCODES.P && hasNoneFocusedElement) {
        EventBus.$emit('start-presenting-now')
      }
    },
    closeBeTakenOverModal() {
      EventBus.$emit('closePresentationPreview')
      this.isShowBeTakenOverModal = false
    },

    preloadListImagePreview() {
      const slideTypePreview = {}
      constants.allTypeSections.forEach((item) => {
        item.slideTypes.forEach((objType) => {
          if (objType.previewUrlVisual) {
            slideTypePreview[objType.preview] = {
              type: objType.preview,
              url: objType.previewUrlVisual,
            }
          }
        })
      })

      const promiseAll = []
      Object.keys(slideTypePreview).forEach((key) => {
        if (slideTypePreview[key].url.includes('webm')) {
          promiseAll.push(Utils.preloadVideo(slideTypePreview[key]))
        } else {
          promiseAll.push(Utils.preloadImage(slideTypePreview[key]))
        }
      })
      Promise.all(promiseAll).then((results) => {
        results.forEach((item) => {
          slideTypePreview[item.type] = item
        })
        this.$store.state.slideTypePreview = slideTypePreview
      })
    },

    _isPresentationEditorPage(route) {
      return route.name === 'PresentationDetail' || route.name === 'PresentationBackstage' || this.isEnableEditPresentationWithOtherRouter || route.name === 'PresentationDetailGoogleSlide'
    },

    joinCollaborativeEditingRoom(presentation) {
      // AHA-5534: Only join presenter collaborative room if active route
      // is the actual Presentation Editor (not the Share page, or the Presenting mode one)
      if (!presentation) return
      if (!presentation.id) return
      if (!presentation.uniqueAccessCode) {
        Sentry.captureMessage(`uniqueAccessCode not found. PresentationId: ${presentation.id}`, 'debug')
        return
      }
      if (
        this._isPresentationEditorPage(this.$route)
      ) {
        socketHelper.joinRoomPresentation(presentation.uniqueAccessCode)
        socketHelper.joinRoomSlide(presentation.uniqueAccessCode, presentation.activeSlide)
      }
    },

    async getDataPresentationDetail(trigger) {
      const { id: presentationId } = this.$route.params

      const payloadDataPresentation = {
        socketId: Socket.getSocketId(),
        admin: this.isAdmin,
      }

      try {
        const res = await this.getPresentationByIdAction({ presentationId, payloadDataPresentation })
        await this.handleDataPresentationDetail(res)

        const { presenting, preview: isPreviewPresentation } = this.$route.query

        // AHA-33599: Track presenting mode issues
        PresentationUtils.trackPresentingModeIssues(res.body)

        if (isPreviewPresentation) {
          this.$store.state.isPreview = true
        }

        // If reload, presentation's presenting -> set fullscreen mode, else set edit mode
        const isPresentationOwnerAndPresenting = !presenting && presentationId &&
          !isPreviewPresentation && !this.isAdmin && this.presentation.presenting &&
            this.currentUser.id == this.presentation.takenOverBy && !this.isEnableAddinPowerpoint
        const isRememberedFullscreenNotDemoMode = this.currentUser.rememberSettings &&
        this.currentUser.rememberSettings.fullscreen && !this.isPlayDemoMode
        GetDataPresentationDetailUtils.checkToSetFullScreenMode(isPresentationOwnerAndPresenting,
          isRememberedFullscreenNotDemoMode, this)

        // When play quiz, reload -> back to lobby screen
        if (!this.isAdmin && this.isQuizNotLobbyScreen) {
          await GetDataPresentationDetailUtils.updateSlideToQuizLobbyScreen(this, trigger)
        }
        // handle when presenting need update on db
        if ((presenting && !this.presentation.presenting &&
            !this.isAdmin && !this.isBackstage) || this.$store.state.isMSTeamsApp) {
          this._updatePresentingMode()
        }
        GetDataPresentationDetailUtils.checkShowQuizCorrectAnswerIconReminder(this)
        EventBus.$emit('updateLastSlide')
        try {
          const isPptPresentation = Utils.getParamValue('ppt-presentation')
          if (isPptPresentation) {
            this.$store.state.activePresentationPowerpointId = presentationId
          }
        } catch (e) {
          Sentry.captureException(e)
          // do nothing
        }
        try {
          this.getLimitationPresentationAction(presentationId)
          this.recheckAndFixCorrectOrderSlidesAction()
        } catch (e) {
          Sentry.captureException(e)
          // do nothing
        }
      } catch (err) {
        GetDataPresentationDetailUtils
          .handleGetDataPresentationDetailFailed(err, this, presentationId)
        if (this.isEnableAddinPowerpoint) {
          await office.saveDataToSlidePowerPoint('dataSlidePPT', null)
          office.setDataSlideInPowerpointToState(null, null)
        }
        Sentry.captureException(new Error('Get Presentation Error'), {
          extra: {
            err,
            presentationId,
            userId: this.currentUser.id,
          },
        })
      }
    },

    _handleShowResultsOnQuizSlide() {
      if (this.presentation.manualRevealCorrectAnswers && !this.slideActive.showCorrectOption) {
        return
      }
      if (this.isTypeAnswerSlideType) {
        EventBus.$emit('eventShowQuizTypeAnswer')
      }
      setTimeout(() => {
        this.updateSlideActiveAction({
          showCorrectOption: true,
          hideResult: false,
        })
      }, 2500)
    },

    _updatePresentingMode() {
      if (!this.$store.state.slideActive.id || !this.$store.state.presentation.id) return
      if (Utils.getParamValue('report', window.location.href) === 'open') return
      if (this.isEnableAddinPowerpoint) {
        // this.$store.state.presentation.presenting = true
        return
      }
      const payload = {
        presenting: true,
        socketId: Socket.getSocketId(),
        activeSlidePreseting: this.$store.state.slideActive.id,
        activeSlideAccessCode: this.$store.state.presentation.uniqueAccessCode,
      }
      if (this.$store.state.isMSTeamsApp) {
        payload.msTeams = true
      }
      if (this.isZoomReady) {
        payload.zoomApp = true
      }
      this.$http
        .put(Config.api.updatePresentation + this.$store.state.presentation.id, payload)
        .then(() => {
        }, () => {})
      this.$store.state.presentation.presenting = true
    },

    _checkAndUpdateHasResults() {
      // if (!this.presentation.hasResults) {
      //   this._updatePresentationHasResults(true)
      // }
    },

    async handleDataPresentationDetail(res) {
      const _dispatch = this.$store.dispatch
      const noAudienceAdmissionFields = (res.body.audienceAdmission &&
        res.body.audienceAdmission.isAudienceAdmission &&
        !res.body.audienceAdmission.fields) || !res.body.audienceAdmission

      this.sockets[Socket.getSocketId()] =
        PresentationDetailUtils.setPresentationSockets(this.currentUser)

      res.body.slides = res.body.Slides

      this.patchPresentationAction({
        submitSlideLookup: res.body.submitSlideLookup || null,
      })

      await handleLoadAllImageCache(res.body.slides)

      this.$store.state.userOnline = res.body.userOnline

      this.$store.state.onlineStatus = PresentationDetailUtils.setOnlineStatus(this.currentUser,
        this.sharePresentation, this.userOnline, this.onlineStatus, this.plan)

      PresentationDetailUtils.setPresentationLanguage(res, _dispatch)

      PresentationDetailUtils.setBrowserTitle(res, this.isPresenterWhiteLabel, this.isBackstage)
      PresentationDetailUtils.sortAndUpdateSlides(res, this)
      this.$store.dispatch('setPresentation', res.body)

      // Fetch and sync slide annotations after presentation data is loaded
      if (this.canUseSlideAnnotations) {
        this.syncAnnotationsAction()
      }

      if (noAudienceAdmissionFields) {
        this._setDefaultFields(res.body)
        this.updatePresentation({
          audienceAdmission: this.presentation.audienceAdmission,
        })
      }

      if (this.integrationPlatform === 'GoogleSlide' && !Utils.getParamValue('presenting')) {
        // do nothing
      } else {
        PresentationDetailUtils.handleActiveTab(this, _dispatch, res)
      }
      EventBus.$emit('setPresentationDataDone')

      if (!res.body.activeSlide && res.body.slides.length) {
        if (!res.body.slides[0].type) {
          this.$store.state.isNewPresentation = true
        }
      }

      PresentationDetailUtils.setPrezFontFamily(this.presentation.fontFamily)

      PresentationDetailUtils.getAudienceOnline(res, this.slideActive, this.presentation)
      OpenAIUtils.setAISlideCountForTracking(res.body, this)
      this.handleCacheImage(res.body)
      this.getQuestions(res.body.id, res.body.slides)
      await RenderCanvasBlocksUtils.loadContentSlidesCanvasBlocks(this, res)
      setTimeout(async () => {
        this.$store.state.renderPresentationDetail = true
        // EventBus.$emit('eventHandleSetValueTransition')
      }, 500)
      PresentationDetailUtils.detectScreenAndSetTransition()
      this.$emit('callBackAfterLoadedPresentation')
    },
    broadcastUpdatePlan() {
      try {
        const channel = new BroadcastChannel('ahaslides-upgrade-plan-channel')

        channel.addEventListener('message', (event) => {
          if (event.data === 'updatePlan') {
            this.$store.dispatch('getCurrentUserAction')
          }
        })
      } catch (e) {
        Sentry.captureException(e)
      }
    },

    _setDefaultFields(detailPresentation) {
      const arr = []
      if (!detailPresentation.audienceAdmission) {
        detailPresentation.audienceAdmission = {}
      }
      if (detailPresentation.audienceAdmission && !detailPresentation.audienceAdmission.heading) {
        this.$store.state.presentation.audienceAdmission.heading = this.$i18nPresenter.t('welcomeToMyPresentation')
      }
      Object.values(constants.audienceAdmissionFields).forEach((item) => {
        this._setLabelToLanguage(item)
        arr.push(item)
      })
      this.$store.state.presentation.audienceAdmission.fields = arr
    },

    _setLabelToLanguage(option) {
      if (option.type === 'name') {
        option.label = this.$i18nPresenter.t('enterYourName')
      }
      if (option.type === 'team') {
        option.label = this.$i18nPresenter.t('enterYourOrganisation')
      }
      if (option.type === 'email') {
        option.label = this.$i18nPresenter.t('enterYourEmail')
      }
    },
    syncWorkingMode() {
      const currentMode = window.localStorage.getItem(`${constants.workspace.WORKING_MODE}-${this.currentUser.id}`)
      if (currentMode && this.$store.state.workingMode !== currentMode) {
        this.$store.state.workingMode = currentMode
      }
    },

    async _updatePresentationHasResults(hasResults) {
      const payload = {
        hasResults,
      }
      PresentationUtils.updatePresentationHasResults(this.presentation, hasResults)
      setTimeout(() => {
        this.patchPresentationAction(payload)
      }, 500)
    },

    initSocket(channel) {
      PresentationEditorSocketHandler.initSocket(channel, this)
    },

    _setTeamData: _.debounce(function () {
      const { teamSize } = this.$store.state.presentation
      const teamCount = this._getTeamCount()
      const teamData = {}
      Object.keys(teamCount)
        .forEach((id) => {
          teamData[id] = {
            teamCount: teamCount[id],
            full: teamCount[id] >= teamSize,
          }
        })
      this.$http.post(Config.api.checkQuizTeamMember, {
        teamData,
        accessCode: this.$store.state.presentation.uniqueAccessCode,
      })
        .then(() => {}, () => {})
    }, 500),

    _getTeamCount() {
      const teams = {}

      Object
        .keys(this.$store.state.audiences)
        .forEach((key) => {
          const teamId = this.$store.state.audiences[key].audienceTeam

          teams[teamId] = teams[teamId] || 0
          teams[teamId] += 1
        })

      return teams
    },

    _getAndSetPayloadAudienceTeamName(audience) {
      this._setTeamData()
      const { teamData } = this.$store.state.presentation
      const audienceTeam = teamData.find((team) => team.id === audience.audienceTeam)
      if (audienceTeam) audience.audienceTeamName = audienceTeam.name
    },

    _handleEventAudienceOnOff: _.throttle(function () {
      if (this.slideActive.type === 'spinnerWheel') {
        EventBus.$emit('updateEntries')
      }
    }, 1000),

    _getAudienceOnlineJoinQuiz: _.throttle(function () {
      let count = 0
      Object.keys(this.$store.state.audiences).forEach((key) => {
        if (!this.$store.state.audiences[key].offline) {
          count++
        }
      })
      this.$store.state.countPlayer = count
      this._getGameLobbyQuiz()
    }, 250),

    _getGameLobbyQuiz: _.throttle(function (doc) {
      if (!this.$store.state.presentation.teamPlay) {
        EventBus.$emit('quiz-bus', doc)
      } else {
        EventBus.$emit('gamelobby-quiz-team', doc)
      }
    }, 1000),

    updatePresentation(payload) {
      this.$http
        .put(Config.api.updatePresentation + this.$route.params.id, payload)
    },

    handleCacheImage(doc) {
      const arr = []
      if (doc && doc.slides) {
        if (doc.logo) {
          arr.push(doc.logo.thumbnail)
          arr.push(doc.logo.large)
        }

        doc.slides.forEach((slide) => this.buildImagesList(slide, arr))
      }

      this.preloadImages(arr)
    },

    buildImagesList(slide, arr) {
      if (slide.image) {
        arr.push(slide.image.thumbnail)
        arr.push(slide.image.xLarge)
      }

      if (slide.backgroundImage) {
        arr.push(slide.backgroundImage.thumbnail)
        arr.push(slide.backgroundImage.xLarge)
      }

      slide.SlideOptions.forEach((option) => {
        if (option.image) {
          arr.push(option.imageCropped)
        }
      })
    },

    preloadImages(array) {
      if (!this.preloadImages.list) {
        this.preloadImages.list = []
      }
      const { list } = this.preloadImages
      for (let i = 0; i < array.length; i++) {
        const img = new Image()
        img.onload = (() => {
          const index = list.indexOf(this)
          if (index !== -1) {
            // remove image from the array once it's loaded
            // for memory consumption reasons
            list.splice(index, 1)
          }
        })
        list.push(img)
        img.src = array[i]
      }
    },

    getQuestions(id) {
      this.$http
        .get(Config.api.getQuestion, {
          params: {
            id,
          },
        })
        .then(
          (res) => {
            Utils.updateDataQuestionAnswer(
              res.body,
              this.$store.state.tabQnaQuestion,
            )
          },
          () => {},
        ).catch((err) => {
          this.$router.push('/apps/presentations')
          this.setErrorOnInternalServerAction('Failed to get questions list')
          throw err
        })
    },

    getQuizIndex(id) {
      if (!id) return null
      const quizs = this.$store.state.presentation.slides.filter((item) => {
        return item.type == 'pickAnswer'
      })
      const index = Utils.getIndexSlide(quizs, id)
      return {
        index: index + 1,
        totalQuiz: quizs.length,
      }
    },

    async handleUpdateNextSlide(idSlide, source) {
      const presentationId =
        this.$store.state.presentation.id || this.$route.params.id
      if (!idSlide || !presentationId) {
        return
      }

      if (this.$store.state.isDisableEditPresentation && source !== 'fromClearDataSlide') {
        const payloadNextSlide = {
          accessCode: this.$store.state.presentation.uniqueAccessCode,
          nextSlideId: idSlide,
        }
        this.$http.post(Config.api.collabNextSlide, payloadNextSlide)
        return
      }

      const indexSlide = this.$store.state.presentation.slides.findIndex((item) => {
        return item.id === idSlide
      })

      this.$store.dispatch('setSyncPresentation', 1)
      const payloadSlide = {
        activeSlide: idSlide,
        socketId: Socket.getSocketId(),
        accessCode: this.$store.state.presentation.uniqueAccessCode,
        timestamp: new Date().getTime(),
        source,
        from: 'handleUpdateNextSlide',
      }

      if (this.$store.state.slideActive.quizStatus !== -1) {
        payloadSlide.quizStatus = constants.QuizStatuses.LOBBY_SCREEN
      }

      let objQuiz = null
      if (this.$store.state.presentation.slides &&
      this.$store.state.presentation.slides[indexSlide] &&
      this.$store.state.presentation.slides[indexSlide].type === 'pickAnswer') {
        objQuiz = this.getQuizIndex(idSlide)

        if (objQuiz) {
          payloadSlide.questionCount = objQuiz.totalQuiz
          payloadSlide.questionIndex = objQuiz.index
        }

        if (this.$store.state.continueQuiz && source !== 'fromClearDataSlide') {
          payloadSlide.quizStatus = constants.QuizStatuses.RULE_SCREEN
        }
      }
      this.$store.state.swapLeaderboard = false
      if (this.$store.state.presentation.slides[indexSlide].type === 'leaderboard') {
        this.$store.dispatch('setSyncPresentation', 2)
        return
      }
      try {
        await SlideApi.updateNextSlideById(presentationId, payloadSlide)
        this.checkSlideMovingDirectionAction(this.slideActive.id)
        EventBus.$emit('enableQuizStart', idSlide)
        this.$store.dispatch('setSyncPresentation', 2)
      } catch (e) {
        this.$store.dispatch('setSyncPresentation', 3)
        Sentry.captureException(e)
      }
    },

    openSlideSearchPopup(pos) {
      this.arrowPos = pos
      this.$store.state.isShowingSlideSearchPopup = true
      this.patchPresentationAction({
        isShowingSlideSearch: true,
      })
    },
    async detectPowerpoint() {
      try {
        if (!this.isEnableAddinPowerpoint) return
        if (window.location.href.indexOf('/presentation/') !== -1) {
          await office.loadOffice()
        }
      } catch (error) {
        Sentry.captureException(error)
      }
    },

    clickOnCloseDocToSlideImportModal() {
      this.setDocToSlideResponseErrorAction(null)
      this.toggleDocToSlideImportModalAction(false)
    },

    resetAIConversation() {
      this.resetPromptToSlideRequestAction()
      this.resetDocToSlideRequestIdAction()
      this.resetConversationAction()
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";
.editor__background {
  @apply flex flex-1 min-h-0;
  background-color: #f0f2f5;
}
.middle-side-right__wrapper {
  @apply relative flex min-h-0 ml-auto;
  flex: 0 1 calc(100% - $left-nav-width);
  &.powerpoint-addin {
    max-width: calc(100% - $left-nav-width);
  }
}
.aha-presentation-editor-wrapper {
  @apply flex flex-col h-full absolute w-full;

  &.show-grid-slide {
    .aha-left-column {
      width: calc(100% - $right-nav-width);
    }
  }
  &.show-grid-slide.right-nav--opening {
    .aha-left-column {
      width: calc(100% - $opened-right-nav-width);
    }
  }
  &.powerpoint-addin {
    min-width: 840px;
  }
}

.aha-presentation-editor-header.in-demo-mode {
  @apply mt-10;
}
.aha-presentation-editor-header {
  &.ppt-header {
    z-index: 11;
  }
}

.aha-left-column {
  @apply absolute left-0 z-5;
  height: calc(100% - $grid-view-button-height);
  transition: all .3s ease;
}
.take-over--side-right {
  display: none;
  @media (max-width: 768px) {
    display: none;
  }
}

</style>
